import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { Tab, Nav, Row, Col, Modal } from 'react-bootstrap';
import { SubNav } from '../layout/submenu';
import { getEquipmentList, deleteEquipment } from '../../actions/equipment';
import { planAnalyzeInfo } from '../../actions/analyze';
import { Modal as modal } from '../widgets';
import { searchWord, isToday, usePermission } from '../../helpers/common';
import {
    EQUIPMENT_TRUCK, EQUIPMENT_TRACTOR,
    EQUIPMENT_IMPLEMENT, EQUIPMENT_SELF_PROPELLED, EQUIPMENT_HANDLING, EQUIPMENT_SCALE, EQUIPMENT_REMOTECONTROL
} from '../../constants';
import EquipmentForm from './EquipmentForm';
import EquipmentQRInfo from '../EquipmentGroupKanban/Form/EquipmentQRInfo';
import { Unit } from '../../helpers/unit';

const EquipmentSubList = () => {
    const tabs = [
        { id: 1, label: "All", equimentTypeIds: null },
        { id: 2, label: "Tractors", equimentTypeIds: [EQUIPMENT_TRACTOR] },
        { id: 3, label: "Trucks", equimentTypeIds: [EQUIPMENT_TRUCK] },
        { id: 4, label: "Implements", equimentTypeIds: [EQUIPMENT_IMPLEMENT, EQUIPMENT_HANDLING, EQUIPMENT_SCALE, EQUIPMENT_REMOTECONTROL] },
        { id: 5, label: "Others", equimentTypeIds: [EQUIPMENT_SELF_PROPELLED] }
    ];

    const { farmId } = useParams();
    const match = useRouteMatch();
    const history = useHistory();
    const dispatch = useDispatch();
    const searchInput = useRef(null);

    const [activeTab, setActiveTab] = useState(1);
    const [searchText, setSearchText] = useState('');
    const [equipmentId, setEquipmentId] = useState('');
    const [equipmentName, setEquipmentName] = useState('');
    const [rootUrl, setRootUrl] = useState('');
    const [showModal, setShowModal] = useState({ add: false, edit: false, delete: false });
    const [showQRMoadl, setShowQRModal] = useState(false);
    const [QRInfo, setQRInfo] = useState({ id: null, name: null, subName: null });
    const [QRFilterOptions, setQRFilterOptions] = useState([]);
    const unit = Unit.getInstance();    

    const isRequesting = useSelector(state => state.equipment.isRequesting);
    const equipmentList = useSelector(state => state.equipment.equipmentList);
    const search = useSelector(state => state.analyze.searchParams);
    const weightShortName = useSelector(state => state.user.settings.weightShortName);

    const allow = usePermission();
    const isAllowCreate = allow("equipment", "create") ? isToday(search.range) : false;
    const isAllowEdit = allow("equipment", "edit");
    const isAllowDelete = allow("equipment", "delete");
    const today = isToday(search.range);

    useEffect(() => {
        if (searchInput && searchInput.current) searchInput.current.focus();
        setRootUrl(`/home/farm/${farmId}/equipment`);
        dispatch(planAnalyzeInfo(farmId));
        dispatch(getEquipmentList({ farmId }));
    }, [farmId, dispatch]);

    useEffect(() => {
        if (equipmentList && equipmentList.length > 0) {
            const filtered = equipmentList.filter(e => e.equipment_type_id === 3);
            if (filtered && filtered.length) {
                setQRFilterOptions(filtered.map(e => ({
                    id: e.equipment_uuid ? e.equipment_uuid : null,
                    name: e.name ? e.name : null,
                    subName: e.equipment_category_name ? e.equipment_category_name : null
                })));
            }
        }
    }, [equipmentList]);

    useEffect(() => {
        dispatch(getEquipmentList({ farmId }));
    }, [search.rangeId, search.range, search.customRange, search.custom, dispatch, farmId]);

    const handleSearchBox = (text) => setSearchText(text);

    const handleSelect = (id) => history.push(`${match.url}/${id}?t=sub&sub=1`);
    
    const handleEquipmentModal = (type, show, id = null, name = "") => {
        setShowModal({
            add: false,
            edit: false,
            delete: false,
            [type]: show
        });    
        setEquipmentId(id);
        setEquipmentName(name);
    };

    const handleDeleteConfim = () => {
        dispatch(deleteEquipment({ farm_uuid: farmId, equipment_uuid: equipmentId }));
    };

    const handleQRModal = (show, data = null) => {
        if (data) {
            setQRInfo({
                id: data.equipment_uuid ? data.equipment_uuid : null,
                name: data.name ? data.name : null,
                subName: data.equipment_category_name ? data.equipment_category_name : null
            });
        } else {
            setQRInfo(null);
        }
        setShowQRModal(show);
    };

    const getFilteredList = () => {
        let filtered = [...(equipmentList || [])];
        const tab = tabs.find(t => t.id === activeTab);
        if (tab && tab.equimentTypeIds)
            filtered = filtered.filter(e => tab.equimentTypeIds.includes(e.equipment_type_id));
        if (searchText.trim().length)
            filtered = filtered.filter(e => searchWord(e.name, searchText));
        return filtered;
    };

    const filteredList = getFilteredList();

    return (
        <SubNav>
            <SubNav.Subheader>
                <div className='title-container'>
                    <aside className='section-title'>
                        <NavLink to={`${rootUrl}/list`}>
                            Equipment {equipmentList && equipmentList.length > 0 ? `(${equipmentList.length})` : ''}<i className="fa fa-angle-right section-right-arrow" />
                        </NavLink>
                    </aside>
                    {isAllowCreate && (
                        <aside className='section-icon-container'>
                            {(activeTab === EQUIPMENT_TRUCK && QRFilterOptions && QRFilterOptions.length > 0) &&
                                <SubNav.QRButton onClick={() => handleQRModal(true)} />}
                            <SubNav.AddButton title="Add Equipment" onClick={() => handleEquipmentModal("add", true)} />
                        </aside>
                    )}
                </div>
            </SubNav.Subheader>

            <SubNav.SearchBox
                ref={searchInput}
                placeholder="Equipment name"
                value={searchText}
                onChange={e => handleSearchBox(e.target.value)}
                onClear={() => handleSearchBox("")}
            />

            <Tab.Container activeKey={activeTab}>
                <Nav variant="tabs">
                    {tabs.map(tab =>
                        <Nav.Item key={tab.id}>
                            <Nav.Link eventKey={tab.id} onClick={() => setActiveTab(tab.id)}>
                                {tab.label}
                            </Nav.Link>
                        </Nav.Item>
                    )}
                </Nav>

                <Tab.Content>
                    <Tab.Pane eventKey={activeTab} className="scroll-bar-2">
                        {isRequesting && <SubNav.Loading />}
                        {!isRequesting && filteredList.length === 0 && <SubNav.NotFound />}
                        {!isRequesting && filteredList.length > 0 &&
                            <SubNav.List>
                                {filteredList.map(e => {
                                    const specification = e.specification ? JSON.parse(e.specification) : null;
                                    return (
                                        <SubNav.Item
                                            key={e.equipment_uuid}
                                            className={`highlight ${e.equipment_uuid === equipmentId ? "selected" : ""}`}>
                                            <SubNav.Card
                                                className={`${e.alive ? "" : "alive-default"}`}
                                                onClick={() => handleSelect(e.equipment_uuid)}>
                                                <Row className="p-0 m-0">
                                                    <Col md={9}>
                                                        <SubNav.Name className="word-wrap" bullet>{e.name}</SubNav.Name>
                                                    </Col>
                                                    <Col md={3}>
                                                        {today && e.alive &&
                                                            <SubNav.ButtonGroup>
                                                                {isAllowEdit && <SubNav.EditButton onClick={() => handleEquipmentModal("edit", true, e.equipment_uuid)} />}
                                                                {isAllowDelete && <SubNav.DeleteButton onClick={() => handleEquipmentModal("delete", true, e.equipment_uuid, e.name)} />}
                                                            </SubNav.ButtonGroup>}
                                                    </Col>
                                                </Row>
                                                <Row className="p-0 m-0 mb-1">
                                                    <SubNav.Display2>
                                                        <div className="d-flex">
                                                            {e.equipment_category_name &&
                                                                <span>
                                                                    <i className="fa fa-truck i-category" />{e.equipment_category_name}
                                                                </span>}
                                                            {specification && specification.capacity &&
                                                                <>
                                                                    <SubNav.Pipe />
                                                                    <span>{`${unit.toRoundOfWeight(specification.capacity)} ${weightShortName}`}</span>
                                                                </>}
                                                        </div>
                                                    </SubNav.Display2>
                                                </Row>
                                                <Row className="p-0 m-0">
                                                    <SubNav.DateRange start={e.start_date} end={e.end_date} alive={e.alive} />
                                                </Row>
                                            </SubNav.Card>
                                        </SubNav.Item>
                                    );
                                })}
                            </SubNav.List>}
                    </Tab.Pane>
                </Tab.Content>

                {showModal.delete && <modal.Confirm
                    show
                    onConfirm={handleDeleteConfim}
                    iconClassName="fa fa-trash"
                    body={`Do you really want to delete this ${equipmentName}?`}
                    onHide={() => handleEquipmentModal("delete", false)}
                />}
                {(showModal.add || (showModal.edit && equipmentId)) &&
                    <Modal show backdrop="static" className="equipment-modal-form">
                        <EquipmentForm
                            onHide={() => handleEquipmentModal("add", false)}
                            farmId={farmId}
                            {...(equipmentId && { equipmentId })}
                        />
                    </Modal>}
                {showQRMoadl &&
                    <EquipmentQRInfo
                        show={showQRMoadl}
                        onHide={() => handleQRModal(false)}
                        QRType="EQUIPMENT_TRUCK"
                        showFilter
                        filterOptions={QRFilterOptions}
                        data={QRInfo}
                        placeholder="Truck"
                    />}
            </Tab.Container>
        </SubNav>
    );
};

export default EquipmentSubList;